import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, Send, Instagram, MessageCircle, Linkedin } from 'lucide-react';
import { contactAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [contactInfo, setContactInfo] = useState({
    email: 'thedevdebojit@gmail.com',
    phone: '+91 954797 1111',
    social_links: {
      instagram: 'debojit_dev14',
      telegram: 'coder_debojit',
      linkedin: 'https://www.linkedin.com/in/debojit-guchhait/',
      github: '',
      twitter: ''
    }
  });
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await contactAPI.submit(formData);
      toast.success('Message sent successfully!');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Error submitting contact form:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24" data-testid="contact-page">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-6" data-testid="contact-title">
            Get In Touch
          </h1>
          <p className="text-base md:text-lg text-muted-foreground mb-16">
            Have a project in mind? Let's work together.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-semibold font-['Syne'] mb-8">Contact Information</h2>
              
              <div className="space-y-6" data-testid="contact-info">
                <div className="flex items-start gap-4">
                  <Mail className="text-primary mt-1" size={24} />
                  <div>
                    <p className="font-medium mb-1">Email</p>
                    <a 
                      href={`mailto:${contactInfo?.email || 'thedevdebojit@gmail.com'}`}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      data-testid="contact-email"
                    >
                      {contactInfo?.email || 'thedevdebojit@gmail.com'}
                    </a>
                  </div>
                </div>

                {contactInfo?.phone && contactInfo.phone.trim() !== '' && (
                  <div className="flex items-start gap-4">
                    <Phone className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium mb-1">Phone</p>
                      <a 
                        href={`tel:${contactInfo.phone}`}
                        className="text-muted-foreground hover:text-primary transition-colors"
                        data-testid="contact-phone"
                      >
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>
                )}

                {contactInfo?.social_links?.instagram && contactInfo.social_links.instagram.trim() !== '' && (
                  <div className="flex items-start gap-4">
                    <Instagram className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium mb-1">Instagram</p>
                      <a 
                        href={`https://instagram.com/${contactInfo.social_links.instagram.replace('@', '').replace('https://instagram.com/', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        data-testid="contact-instagram"
                      >
                        @{contactInfo.social_links.instagram.replace('@', '').replace('https://instagram.com/', '')}
                      </a>
                    </div>
                  </div>
                )}

                {contactInfo?.social_links?.telegram && contactInfo.social_links.telegram.trim() !== '' && (
                  <div className="flex items-start gap-4">
                    <MessageCircle className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium mb-1">Telegram</p>
                      <a 
                        href={`https://t.me/${contactInfo.social_links.telegram.replace('@', '').replace('https://t.me/', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        data-testid="contact-telegram"
                      >
                        @{contactInfo.social_links.telegram.replace('@', '').replace('https://t.me/', '')}
                      </a>
                    </div>
                  </div>
                )}

                {contactInfo?.social_links?.linkedin && contactInfo.social_links.linkedin.trim() !== '' && (
                  <div className="flex items-start gap-4">
                    <Linkedin className="text-primary mt-1" size={24} />
                    <div>
                      <p className="font-medium mb-1">LinkedIn</p>
                      <a 
                        href={contactInfo.social_links.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-primary transition-colors"
                        data-testid="contact-linkedin"
                      >
                        {contactInfo.social_links.linkedin}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="h-12"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="h-12"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-black hover:bg-primary/90 rounded-full h-12 text-base font-medium"
                  data-testid="contact-submit-button"
                >
                  {submitting ? 'Sending...' : (
                    <>
                      <Send size={18} className="mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
